<script setup>
/**
 * The ticket's properties, and every mutation that is one click.
 *
 * One column, one row grammar: a label, and the control that changes it. Stage,
 * owner and archival are each their own server-side command, and priority and
 * the type's custom fields are field edits, so the rail emits five different
 * intents rather than one "save" — the screen that owns the store decides which
 * endpoint each one is.
 *
 * The rail shows only what the model actually carries. There is no due date, no
 * watcher list and no label set on `support_cases`, so there is no row for one;
 * the overdue mark is derived from the stage's own SLA rather than a column.
 *
 * The requester is stated, not edited: who asked is settled when the ticket is
 * opened, and the row is absent on the internal types that never carry one.
 *
 * An archived ticket is refused every write by the server except the restore, so
 * that is exactly what the rail leaves live.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import CaseFieldsPanel from './CaseFieldsPanel.vue';
import CaseTypeBadge from './CaseTypeBadge.vue';
import OwnerChip from './OwnerChip.vue';
import StagePill from './StagePill.vue';
import VisibilityBadge from './VisibilityBadge.vue';
import {
  CASE_PRIORITY,
  CASE_PRIORITY_META,
  casePriorityMeta,
  isOverdueInStage,
  lastActivityLabel,
  presentOwner,
} from 'dashboard/helper/supportCaseMeta';

const props = defineProps({
  supportCase: {
    type: Object,
    required: true,
  },
  // Every active stage of the type's workflow, in position order.
  workflowStages: {
    type: Array,
    default: () => [],
  },
  // The people and the teams a case may be handed to. Owner is one or the
  // other, never both.
  assignableUsers: {
    type: Array,
    default: () => [],
  },
  assignableTeams: {
    type: Array,
    default: () => [],
  },
  isUpdating: {
    type: Boolean,
    default: false,
  },
  // The ticket is archived: every write but the restore is refused.
  isLocked: {
    type: Boolean,
    default: false,
  },
  // Administrators only — the type decides this ticket's workflow, its fields
  // and its audience, so the row that names it is where an admin goes to change
  // it.
  canManageTypes: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'changeStage',
  'assign',
  'updatePriority',
  'updateField',
  'archive',
  'unarchive',
]);

// Most urgent first: the menu is read top-down when something is on fire.
const PRIORITY_ORDER = [
  CASE_PRIORITY.URGENT,
  CASE_PRIORITY.HIGH,
  CASE_PRIORITY.MEDIUM,
  CASE_PRIORITY.LOW,
];

const { t } = useI18n();

const openMenu = ref(null);

const isDisabled = computed(() => props.isUpdating || props.isLocked);

const stage = computed(() => props.supportCase.caseStage || {});

const owner = computed(() => presentOwner(props.supportCase));

const priority = computed(() => props.supportCase.priority ?? null);

const priorityLabel = computed(() => casePriorityMeta(priority.value));

const visibility = computed(() => props.supportCase.visibility || {});

// The breach is a client-side comparison of `stage_entered_at + sla_minutes`;
// no scheduler writes it, so nothing on the row is a stored verdict.
const isOverdue = computed(() => isOverdueInStage(props.supportCase));

const openedAge = computed(() =>
  lastActivityLabel(props.supportCase.createdAt)
);

const openedBy = computed(() => {
  const { creator } = props.supportCase;
  return creator?.availableName || creator?.name || '';
});

const stageItems = computed(() =>
  props.workflowStages.map(workflowStage => ({
    label: workflowStage.name,
    value: workflowStage.id,
    action: 'stage',
    isSelected: workflowStage.id === stage.value.id,
  }))
);

// One menu, two kinds of owner, and "nobody" first — unassigning is a real
// decision, not the absence of one.
const ownerSections = computed(() => [
  {
    items: [
      {
        label: t('SUPPORT_CASES.OWNER.UNASSIGNED'),
        value: null,
        action: 'unassign',
        icon: 'i-lucide-circle-dashed',
        isSelected: !owner.value,
      },
    ],
  },
  {
    title: t('SUPPORT_CASES.DETAIL.OWNER.AGENTS'),
    items: props.assignableUsers.map(user => ({
      label: user.name || user.availableName,
      value: user.id,
      action: 'assignUser',
      thumbnail: { name: user.name || user.availableName, src: user.thumbnail },
      isSelected: props.supportCase.assignee?.id === user.id,
    })),
    emptyState: t('SUPPORT_CASES.DETAIL.OWNER.NO_AGENTS'),
  },
  {
    title: t('SUPPORT_CASES.DETAIL.OWNER.TEAMS'),
    items: props.assignableTeams.map(team => ({
      label: team.name,
      value: team.id,
      action: 'assignTeam',
      icon: 'i-lucide-users',
      isSelected: props.supportCase.assigneeTeam?.id === team.id,
    })),
    emptyState: t('SUPPORT_CASES.DETAIL.OWNER.NO_TEAMS'),
  },
]);

// "No priority" leads, because the column is nullable and clearing it is a
// choice an agent makes rather than a value they fail to pick.
const priorityItems = computed(() => [
  {
    label: t('SUPPORT_CASES.DETAIL.RAIL.PRIORITY_NONE'),
    value: null,
    action: 'priority',
    icon: 'i-lucide-circle-dashed',
    isSelected: !priority.value,
  },
  ...PRIORITY_ORDER.map(value => ({
    label: t(CASE_PRIORITY_META[value].labelKey),
    value,
    action: 'priority',
    icon: CASE_PRIORITY_META[value].icon,
    isSelected: priority.value === value,
  })),
]);

const closeMenu = () => {
  openMenu.value = null;
};

const toggleMenu = key => {
  openMenu.value = openMenu.value === key ? null : key;
};

const onStageSelect = selected => {
  closeMenu();
  const next = props.workflowStages.find(
    workflowStage => workflowStage.id === selected.value
  );
  if (!next || next.id === stage.value.id) return;
  emit('changeStage', next);
};

// Re-picking the owner the ticket already has is a menu closing, not a handover
// worth an assignment activity on the timeline.
const onOwnerSelect = ({ action, value }) => {
  closeMenu();
  if (action === 'assignUser') {
    if (props.supportCase.assignee?.id === value) return;
    emit('assign', { assigneeId: value });
  } else if (action === 'assignTeam') {
    if (props.supportCase.assigneeTeam?.id === value) return;
    emit('assign', { assigneeTeamId: value });
  } else {
    if (!owner.value) return;
    emit('assign', {});
  }
};

const onPrioritySelect = ({ value }) => {
  closeMenu();
  const next = value ?? null;
  if (next === priority.value) return;
  emit('updatePriority', next);
};

// A ticket replaced under the rail leaves no menu open over the wrong record.
watch(() => props.supportCase.id, closeMenu);
</script>

<template>
  <aside
    v-on-click-outside="closeMenu"
    class="flex flex-col gap-4"
    :aria-label="t('SUPPORT_CASES.DETAIL.RAIL.TITLE')"
    data-testid="case-property-rail"
  >
    <span class="text-xs font-medium tracking-wide uppercase text-n-slate-10">
      {{ t('SUPPORT_CASES.DETAIL.RAIL.TITLE') }}
    </span>

    <div class="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-2">
      <span class="text-xs text-n-slate-11">
        {{ t('SUPPORT_CASES.DETAIL.RAIL.STAGE') }}
      </span>
      <div class="relative min-w-0">
        <button
          type="button"
          class="inline-flex items-center w-full min-w-0 gap-1 px-2 py-1 -mx-2 rounded-lg enabled:hover:bg-n-alpha-1 disabled:opacity-50"
          :disabled="isDisabled"
          :aria-label="t('SUPPORT_CASES.DETAIL.CHANGE_STAGE')"
          data-testid="case-rail-stage-button"
          @click="toggleMenu('stage')"
        >
          <StagePill :stage="stage" />
          <Icon icon="i-lucide-chevron-down" class="size-4 text-n-slate-11" />
        </button>
        <DropdownMenu
          v-if="openMenu === 'stage'"
          :menu-items="stageItems"
          class="mt-1 top-full ltr:left-0 rtl:right-0 min-w-52"
          data-testid="case-rail-stage-menu"
          @action="onStageSelect"
        />
      </div>
    </div>

    <Label
      v-if="isOverdue"
      compact
      color="ruby"
      :label="t('SUPPORT_CASES.SLA.OVERDUE_IN_STAGE')"
      data-testid="case-rail-overdue"
    >
      <template #icon>
        <Icon icon="i-lucide-clock-alert" class="flex-shrink-0 size-3" />
      </template>
    </Label>

    <div class="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-2">
      <span class="text-xs text-n-slate-11">
        {{ t('SUPPORT_CASES.DETAIL.RAIL.OWNER') }}
      </span>
      <div class="relative min-w-0">
        <button
          type="button"
          class="inline-flex items-center w-full min-w-0 gap-1 px-2 py-1 -mx-2 rounded-lg enabled:hover:bg-n-alpha-1 disabled:opacity-50"
          :disabled="isDisabled"
          :aria-label="t('SUPPORT_CASES.DETAIL.ASSIGN')"
          data-testid="case-rail-owner-button"
          @click="toggleMenu('owner')"
        >
          <OwnerChip :owner="owner" />
          <Icon icon="i-lucide-chevron-down" class="size-4 text-n-slate-11" />
        </button>
        <DropdownMenu
          v-if="openMenu === 'owner'"
          :menu-sections="ownerSections"
          show-search
          :search-placeholder="t('SUPPORT_CASES.DETAIL.OWNER.SEARCH')"
          class="mt-1 top-full ltr:left-0 rtl:right-0 min-w-56"
          data-testid="case-rail-owner-menu"
          @action="onOwnerSelect"
        />
      </div>
    </div>

    <div class="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-2">
      <span class="text-xs text-n-slate-11">
        {{ t('SUPPORT_CASES.DETAIL.RAIL.PRIORITY') }}
      </span>
      <div class="relative min-w-0">
        <button
          type="button"
          class="inline-flex items-center w-full min-w-0 gap-1 px-2 py-1 -mx-2 rounded-lg enabled:hover:bg-n-alpha-1 disabled:opacity-50"
          :disabled="isDisabled"
          :aria-label="t('SUPPORT_CASES.DETAIL.RAIL.PRIORITY_ARIA')"
          data-testid="case-rail-priority-button"
          @click="toggleMenu('priority')"
        >
          <Label
            v-if="priorityLabel"
            compact
            :color="priorityLabel.color"
            :label="t(priorityLabel.labelKey)"
          >
            <template #icon>
              <Icon :icon="priorityLabel.icon" class="flex-shrink-0 size-3" />
            </template>
          </Label>
          <span v-else class="text-sm text-n-slate-10">
            {{ t('SUPPORT_CASES.DETAIL.RAIL.PRIORITY_NONE') }}
          </span>
          <Icon icon="i-lucide-chevron-down" class="size-4 text-n-slate-11" />
        </button>
        <DropdownMenu
          v-if="openMenu === 'priority'"
          :menu-items="priorityItems"
          class="mt-1 top-full ltr:left-0 rtl:right-0 min-w-52"
          data-testid="case-rail-priority-menu"
          @action="onPrioritySelect"
        />
      </div>
    </div>

    <div
      v-if="supportCase.requesterContact"
      class="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-2"
    >
      <span class="text-xs text-n-slate-11">
        {{ t('SUPPORT_CASES.DETAIL.RAIL.REQUESTER') }}
      </span>
      <span
        class="min-w-0 text-sm truncate text-n-slate-12"
        data-testid="case-rail-requester"
      >
        {{ supportCase.requesterContact.name }}
      </span>
    </div>

    <div
      v-if="supportCase.caseType"
      class="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-2"
    >
      <span class="text-xs text-n-slate-11">
        {{ t('SUPPORT_CASES.DETAIL.RAIL.TYPE') }}
      </span>
      <div class="flex items-center min-w-0 gap-2">
        <CaseTypeBadge :case-type="supportCase.caseType" />
        <router-link
          v-if="canManageTypes"
          :to="{ name: 'settings_ticket_types' }"
          class="text-xs text-n-slate-11 hover:text-n-slate-12"
          data-testid="case-manage-types"
        >
          {{ t('SUPPORT_CASES.SETTINGS.LIST.MANAGE') }}
        </router-link>
      </div>
    </div>

    <div class="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-2">
      <span class="text-xs text-n-slate-11">
        {{ t('SUPPORT_CASES.DETAIL.RAIL.VISIBILITY') }}
      </span>
      <div class="flex min-w-0">
        <VisibilityBadge
          :mode="visibility.mode"
          :team-names="visibility.teamNames || []"
        />
      </div>
    </div>

    <div class="h-px bg-n-weak" aria-hidden="true" />

    <CaseFieldsPanel
      :field-definitions="supportCase.fieldDefinitions || []"
      :values="supportCase.customFields || {}"
      :disabled="isDisabled"
      @update="emit('updateField', $event)"
    />

    <div class="h-px bg-n-weak" aria-hidden="true" />

    <Button
      v-if="isLocked"
      variant="outline"
      color="slate"
      size="sm"
      class="w-full"
      :label="t('SUPPORT_CASES.DETAIL.ACTIONS.UNARCHIVE')"
      :disabled="isUpdating"
      data-testid="case-rail-unarchive"
      @click="emit('unarchive')"
    />
    <Button
      v-else
      variant="outline"
      color="slate"
      size="sm"
      class="w-full"
      :label="t('SUPPORT_CASES.DETAIL.ACTIONS.ARCHIVE')"
      :disabled="isDisabled"
      data-testid="case-rail-archive"
      @click="emit('archive')"
    />

    <span
      v-if="openedAge && openedBy"
      class="text-xs text-n-slate-10"
      data-testid="case-rail-opened"
    >
      {{
        t('SUPPORT_CASES.DETAIL.RAIL.OPENED', {
          age: openedAge,
          name: openedBy,
        })
      }}
    </span>
  </aside>
</template>
